import React, { useState } from 'react';
import FormField from '@ingka/form-field';
import RadioButtonGroup from '@ingka/radio-button-group';
import CookieJar from 'utils/cookieJar';
import { useToast } from 'hooks/useToast';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import { useTranslation } from 'react-i18next';
import ListView, { ListViewItem } from '@ingka/list-view';
import Text from '@ingka/text';
import InlineMessage from '@ingka/inline-message';
import information from '@ingka/ssr-icon/paths/information';
import classes from 'components/UserInfo/UserInfoPanel.module.scss';
import locales from './supportedLanguages.json';

export const getLanguageLabel = (locale: string): string => {
    const [languageCode, regionCode] = locale.split('-');
    const languageName = new Intl.DisplayNames(['en'], { type: 'language', languageDisplay: 'standard' }).of(languageCode);
    const regionName = regionCode ? new Intl.DisplayNames(['en'], { type: 'region' }).of(regionCode) : undefined;

    if (languageName && regionName) {
        return `${languageName} (${regionName})`;
    }

    return locale;
};

export const languagelabels = locales.map(getLanguageLabel).map((label, index) => ({
    id: `user-language-${locales[index]}`,
    value: locales[index],
    label,
}));

const LanguageSelector = () => {
    const languageCookie = CookieJar({ name: 'lang' });
    const { i18n } = useTranslation();
    const { displayToast } = useToast();
    const t = useNestedTranslation('user.profile');
    const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.resolvedLanguage ?? (navigator.language === 'en' ? 'en-GB' : navigator.language));

    const onLanguageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextLanguage = event.target.value;

        i18n.changeLanguage(nextLanguage).then(() => {
            languageCookie.set('lang', nextLanguage);
            setSelectedLanguage(nextLanguage);
        }).catch(() => {
            displayToast({ title: t('ERROR'), message: t('languageChangeFailed') });
        });
    };

    return (
        <div data-testid="user-info-panel-languages">
            <InlineMessage ssrIcon={information} body={t('selectedLanguage')} variant="informative" />
            <div>
                <ListView id="userInfo-panel-browser-language" data-testid="userInfo-panel-browser-language" showDivider>
                    <ListViewItem
                        data-testid="userInfo-panel-browser-language"
                        emphasised
                        description={(
                            <div data-testid="user-info-detail-browser-language">
                                <Text textStyle="Heading.XS">{t('language')}</Text>
                                <Text textStyle="Body.M" className={classes['user-info-panel-detail-data']}>
                                    {getLanguageLabel(selectedLanguage)}
                                </Text>
                            </div>
                        )}
                        size="small"
                    />
                </ListView>
            </div>
            <div className={classes['user-info-panel-languages']} data-testid="userInfo-panel-languages">
                <FormField fieldHelper={{ id: 'helper-msg-id' }}>
                    <RadioButtonGroup
                        list={
                            languagelabels.map(option => ({
                                ...option,
                                checked: option.value === selectedLanguage,
                                onChange: onLanguageChange,
                            }))
                        }
                        name={t('selectLanguage')}
                    />
                </FormField>
            </div>
        </div>
    );
};

export default LanguageSelector;
